import { useEffect, useState, useCallback } from "react"; // Added useCallback for memoization
import useGeneralApi from "../../../config/hooks/api/general/useGeneralApi";
import { Avatar, Card, Col, Pagination, Row } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { FaChevronLeft } from "react-icons/fa6";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  resetUnreadNotificationCount,
  setAppLoading,
} from "../../../store/common/generalSlice";
import {
  capitalizeString,
  filterSpecialCharacters,
} from "../../../config/utils";
import "./style.css";
import notificationAdminLogo from "../../../assets/images/notificationAdminLogo.png";
import favLogo from "../../../assets/images/fav.png";

// OPTIMIZATION: Moved notification cases outside component to prevent recreation on each render
const NOTIFICATION_CASES = [
  {
    type: ["quote", "service", "request"],
    roles: [
      { role: "user", url: "/quote" },
      { role: "provider", url: "/booking" },
    ],
  },
  {
    type: ["job", "requests"],
    roles: [
      { role: "user", url: "/myjobs" },
      { role: "provider", url: "/myjobs" },
    ],
  },
  {
    type: ["quote", "accepted", "user"],
    roles: [
      { role: "user", url: "/booking" },
      { role: "provider", url: "/myjobs" },
    ],
  },
  {
    type: ["quote", "accepted", "provider"],
    roles: [
      { role: "user", url: "/booking" },
      { role: "provider", url: "/booking" },
    ],
  },
  {
    type: ["quote", "rejected", "user"],
    roles: [
      { role: "user", url: "/booking" },
      { role: "provider", url: "/myjobs" },
    ],
  },
  {
    type: ["quote", "rejected", "provider"],
    roles: [
      { role: "user", url: "/booking" },
      { role: "provider", url: "/booking" },
    ],
  },
  {
    type: ["booking", "accepted", "user"],
    roles: [
      { role: "user", url: "/booking" },
      { role: "provider", url: "/myjobs" },
    ],
  },
  {
    type: ["booking", "accepted", "provider"],
    roles: [
      { role: "user", url: "/booking" },
      { role: "provider", url: "/booking" },
    ],
  },
  {
    type: ["booking", "rejected", "user"],
    roles: [
      { role: "user", url: "/booking" },
      { role: "provider", url: "/myjobs" },
    ],
  },
  {
    type: ["booking", "rejected", "provider"],
    roles: [
      { role: "user", url: "/booking" },
      { role: "provider", url: "/booking" },
    ],
  },
  {
    type: ["booking", "request", "completed"],
    roles: [
      { role: "user", url: "/booking" },
      { role: "provider", url: "/booking" },
    ],
  },
  {
    type: ["booking", "amendments"],
    roles: [
      { role: "user", url: "/booking" },
      { role: "provider", url: "/booking" },
    ],
  },
  {
    type: ["dispute", "reported"],
    roles: [
      { role: "user", url: "/booking" },
      { role: "provider", url: "/booking" },
    ],
  },
];

// OPTIMIZATION: Created constants for common notification types
const NOTIFICATION_TYPES = {
  REQUEST_ACCEPTED: "Request Accepted",
  DISPUTE_RESOLVED: "DISPUTE_RESOLVED",
  NEW_QUOTE: "NEW_QUOTE",
  BOOKING_AMEND: "BOOKING_AMEND",
  REQUEST_COMPLETED: "Request Completed",
  SERVICE_REQUEST: "Service Request",
  ADMIN_ANNOUNCEMENT: "ADMIN_ANNOUNCEMENT",
};

// some notification bodies arrive as a json payload instead of plain text,
// e.g. {"message":"new quote available...","quotesource":"admin-assigned",...}
// so we display the message from it and fall back to the raw body.
const getNotificationBodyText = (body) => {
  if (!body || typeof body != "string") return body;

  const trimmedBody = body.trim();
  if (!trimmedBody.startsWith("{")) return body;

  try {
    const parsedBody = JSON.parse(trimmedBody);
    return parsedBody?.message || body;
  } catch (error) {
    return body;
  }
};

// OPTIMIZATION: Created a separate component for notification items to improve readability
const NotificationItem = ({ notification, onClick }) => {
  // ADMIN_ANNOUNCEMENT notifications have no target screen, so the card is not clickable
  const isAdminAnnouncement =
    notification?.type === NOTIFICATION_TYPES.ADMIN_ANNOUNCEMENT;

  return (
    <Card
      bordered={false}
      className={`p-0 mb-2 rounded-md ${isAdminAnnouncement ? "" : "cursor-pointer notification-item"}`}
      key={notification?.id}
      onClick={isAdminAnnouncement ? undefined : () => onClick(notification)}
    >
      <div className="flex items-center space-x-2">
        <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 mr-2">
          <Avatar
            src={
              isAdminAnnouncement
                ? favLogo
                : notification?.body == "You have been assigned to a new job"
                  ? notificationAdminLogo
                  : notification?.user?.imageThumbUrl
            }
            alt="user avatar"
            className="w-full h-full object-cover"
            size={48}
          >
            {notification?.user?.fullName?.charAt(0)?.toUpperCase()}
          </Avatar>
        </div>
        <NotificationBody data={notification} />
      </div>
    </Card>
  );
};

const Notifications = () => {
  const { unreadNotifications } = useSelector((state) => state?.general);
  const { loggedInUser } = useSelector((state) => state?.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { getNotifications } = useGeneralApi();

  const [notifications, setNotifications] = useState([]);
  const [pageSize, setPageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // OPTIMIZATION: Calculate offset from currentPage and pageSize instead of tracking separately
  const offset = (currentPage - 1) * pageSize;

  // OPTIMIZATION: Removed useCallback to prevent dependency cycle
  const getNotificationsList = async () => {
    try {
      dispatch(setAppLoading(true));

      const response = await getNotifications(pageSize, offset);
      if (response) {
        setNotifications(response?.data?.notifications);
        setTotalItems(response?.data?.count);
        dispatch(setAppLoading(false));
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      dispatch(setAppLoading(false));
    }
  };

  // OPTIMIZATION: Simplified page size change handler
  const onShowSizeChange = (current, newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // OPTIMIZATION: Simplified page change handler
  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  // OPTIMIZATION: Refactored navigation logic for special cases into a separate function
  const handleSpecialCaseNavigation = (notificationDetail) => {
    const { type, body, resourceKeyId } = notificationDetail;

    // OPTIMIZATION: Using constants for notification types
    if (type === NOTIFICATION_TYPES.REQUEST_ACCEPTED) {
      const isProviderAction = body?.includes("by Provider");
      const isUserAction = body?.includes("by User");

      if (isProviderAction || isUserAction) {
        navigate(`../myjobs/${resourceKeyId}`);
      } else {
        navigate(`../booking/${resourceKeyId}`);
      }
      return true;
    }

    if (type === NOTIFICATION_TYPES.DISPUTE_RESOLVED) {
      if (loggedInUser?.type === "Service User") {
        navigate(`../booking/${resourceKeyId}`);
      } else {
        navigate(`../myjobs/${resourceKeyId}`);
      }
      return true;
    }

    return false;
  };

  // OPTIMIZATION: Refactored notification navigation logic for better readability and maintainability
  const handleNotificationNavigation = useCallback(
    (notificationDetail) => {
      if (!notificationDetail?.body) return;
      const { type, body, resourceKeyId } = notificationDetail;
      if (type === NOTIFICATION_TYPES.NEW_QUOTE) {
        navigate(`../quote/${resourceKeyId}`);
        return;
      }
      // Handle special cases first
      if (handleSpecialCaseNavigation(notificationDetail)) {
        return;
      }

      // Handle other specific cases
      if (
        body === "You have received a service request by Provider" ||
        body === "New job requests by User" ||
        body === "New job requests by Provider"
      ) {
        navigate(`../myjobs/${resourceKeyId}`);
        return;
      }

      if (
        body === "You have received a service request by" ||
        body === "New job requests by" ||
        "You have been assigned to a new job"
      ) {
        navigate(`../job/${resourceKeyId}`);
        return;
      }

      if (
        type === NOTIFICATION_TYPES.BOOKING_AMEND ||
        type === NOTIFICATION_TYPES.REQUEST_COMPLETED
      ) {
        navigate(`../booking/${resourceKeyId}`);
        return;
      }

      // Handle generic cases using the notification cases array
      const notificationType = filterSpecialCharacters(body).toLowerCase();

      for (const caseItem of NOTIFICATION_CASES) {
        const condition = caseItem.type.every((keyword) =>
          notificationType.split(" ").includes(keyword),
        );

        if (condition) {
          const userType = loggedInUser?.type.toLowerCase();
          const navigationByRole = caseItem.roles.find((role) =>
            userType.includes(role.role),
          );

          if (navigationByRole) {
            navigate(`..${navigationByRole.url}/${resourceKeyId}`);
            break;
          }
        }
      }
    },
    [navigate, loggedInUser],
  );

  // OPTIMIZATION: Added dependency array to useEffect to prevent unnecessary API calls
  useEffect(() => {
    getNotificationsList();
  }, [currentPage, pageSize]); // Changed dependency array to only include currentPage and pageSize

  // OPTIMIZATION: Added dependency array to useEffect
  useEffect(() => {
    if (unreadNotifications && unreadNotifications.length) {
      dispatch(resetUnreadNotificationCount());
    }
  }, [unreadNotifications, dispatch]);

  return (
    <Row>
      <Col
        md={24}
        sm={24}
        xs={24}
        className="md:px-20 sm:px-20 md:pt-6 sm:pt-6 px-2 pt-6"
      >
        <div className="flex justify-between mb-4">
          <h5 className="heading-5 text-slate-600 font-semibold">
            Notifications
          </h5>
          <Link to="/" className="cursor-pointer flex items-center">
            <FaChevronLeft className="text-primary bg-white p-2 rounded-full text-3xl" />
            <small className="ml-2 font-medium">Back</small>
          </Link>
        </div>

        {/* OPTIMIZATION: Added empty state message */}
        {notifications && notifications.length ? (
          notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onClick={handleNotificationNavigation}
            />
          ))
        ) : (
          <Card bordered={false} className="p-4 text-center">
            <p>No notifications to display</p>
          </Card>
        )}
      </Col>

      {/* OPTIMIZATION: Only show pagination if there are items */}
      {totalItems > 0 && (
        <Col span={24} className="md:px-20 sm:px-20 md:pt-6 sm:pt-6 px-2 pt-6">
          <div className="px-2 py-4 bg-white rounded-md">
            <Pagination
              showSizeChanger
              onShowSizeChange={onShowSizeChange}
              current={currentPage}
              total={totalItems}
              pageSize={pageSize}
              pageSizeOptions={[5, 10, 20, 50]}
              onChange={onPageChange}
            />
          </div>
        </Col>
      )}
    </Row>
  );
};

// OPTIMIZATION: Refactored NotificationBody component for better readability and maintainability
const NotificationBody = ({ data }) => {
  const { title, type, user, createdAt } = data;
  const body = getNotificationBodyText(data?.body);
  const formattedDate = dayjs(createdAt).format("YYYY-MM-DD");
  const formattedTime = dayjs(createdAt).format("HH:mm");

  // OPTIMIZATION: Created helper function for common notification body patterns
  const renderStandardBody = (bodyText, showName = true) => (
    <p className="text-sm">
      <span>
        {capitalizeString(bodyText?.toLowerCase())}
        {bodyText == "Your booking has been accepted by" ? "Admin" : null}
      </span>

      {showName && user?.fullName && (
        <span className="font-bold inline-block ml-1">
          {body == "You have been assigned to a new job"
            ? "Admin"
            : capitalizeString(user.fullName)}
        </span>
      )}
    </p>
  );

  const renderNotificationBody = () => {
    if (!type || !data) return null;

    switch (type) {
      case "Service Request":
      case "NEW_QUOTE":
        return renderStandardBody(`${body} from`, true);

      case "Request Accepted":
      case "Request Rejected":
      case "Request Completed":
        return (
          <div className="flex mb-1">{renderStandardBody(body, true)}</div>
        );

      case "POST_LIKE":
      case "POLL_LIKE":
      case "POST_COMMENT":
      case "POLL_COMMENT":
      case "DOCUMENTS_REJECTED":
      case "DOCUMENTS_ALERT":
      case "BOOKING_AMEND":
        return (
          <p className="mb-1 text-sm">
            <span className="font-bold inline-block text-md">
              {capitalizeString(user?.fullName)}
            </span>
            ,
            <span className="inline-block ml-1 mb-[1px]">
              {body && body.toLowerCase()}
            </span>
          </p>
        );

      case "ADMIN_ANNOUNCEMENT":
        return (
          <p className="mb-1 text-sm">
            <span className="font-bold inline-block text-md">
              {capitalizeString(title)}
            </span>
            <span className="block">{body}</span>
            <span className="block text-xs">From Admin</span>
          </p>
        );

      case "REPORT_DISPUTE":
        return renderStandardBody(`${body} by`, true);

      case "ADVERTISEMENT_ALERT":
      case "DISPUTE_RESOLVED":
      case "BUSINESS_INFORMATION_ALERT":
        return renderStandardBody(body, false);

      default:
        return null;
    }
  };

  return (
    <div className="ml-2 flex items-center">
      <div className="flex flex-col text-sm justify-center">
        {renderNotificationBody()}
        <p className="text-xs">{`${formattedDate} ${formattedTime}`}</p>
      </div>
    </div>
  );
};

export default Notifications;
